<template>
  <div class="login-page app-form-surface">
    <div
      class="login-page__backdrop"
      :style="{ '--login-hero-image': `url(${loginHeroImage})` }"
      aria-hidden="true"
    />
    <div class="background-gradient" aria-hidden="true" />

    <!-- Main Content -->
    <div class="login-main">
      <div class="login-container">
        <!-- Left Side - Hero Content -->
        <div class="hero-section">
          <div class="hero-content">
            <div class="brand-section">
              <div class="logo-container">
                <v-icon size="40" color="white" class="logo-icon">mdi-music-note</v-icon>
              </div>
              <h1 class="hero-title font-display">
                Professional <span class="accent-text">Audition Management</span> for the Modern Stage
              </h1>
              <p class="hero-subtitle">
                Join thousands of companies using Cuetrics to streamline their audition process, 
                manage performers, and find the perfect talent with real-time scoring and comprehensive tools.
              </p>
            </div>
            
            <!-- Feature highlights -->
            <div class="features-grid">
              <div class="feature-item">
                <span class="feature-icon-wrap" aria-hidden="true">
                  <v-icon size="22" class="feature-icon">mdi-chart-line</v-icon>
                </span>
                <div class="feature-content">
                  <h3 class="feature-title">Real-time Scoring</h3>
                  <p class="feature-description">Live judging with instant feedback and aggregation</p>
                </div>
              </div>
              <div class="feature-item">
                <span class="feature-icon-wrap" aria-hidden="true">
                  <v-icon size="22" class="feature-icon">mdi-video</v-icon>
                </span>
                <div class="feature-content">
                  <h3 class="feature-title">Video Recording</h3>
                  <p class="feature-description">Capture auditions with high-quality video and audio</p>
                </div>
              </div>
              <div class="feature-item">
                <span class="feature-icon-wrap" aria-hidden="true">
                  <v-icon size="22" class="feature-icon">mdi-account-multiple</v-icon>
                </span>
                <div class="feature-content">
                  <h3 class="feature-title">Talent Management</h3>
                  <p class="feature-description">Comprehensive performer profiles and materials</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side - Login Form -->
        <div class="form-section">
          <div class="form-container">
            <!-- Form Header -->
            <div class="form-header">
              <img :src="cuetricsLogo" alt="CUETRICS" class="form-brand-logo" />
              <!-- <h2 class="form-title font-display">Welcome back</h2> -->
              <p class="form-subtitle">Sign in to your account</p>
            </div>

            <!-- Login Form -->
            <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit" class="login-form">
              <v-text-field
                v-model="form.email"
                type="email"
                :rules="[rules.required, rules.email]"
                outlined
                dense
                dark
                required
                label="Email address"
                placeholder="Enter your email"
                class="mb-4 login-field"
              />

              <v-text-field
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                :rules="[rules.required]"
                outlined
                dense
                dark
                required
                autocomplete="current-password"
                label="Password"
                placeholder="Enter your password"
                :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="showPassword = !showPassword"
                :class="['mb-4', 'login-field']"
              />

              <!-- Error Display -->
              <v-alert
                v-if="hasError"
                type="error"
                class="error-alert"
                dense
                text
              >
                {{ error }}
              </v-alert>

              <!-- Remember Me & Forgot Password -->
              <div class="form-options">
                <v-checkbox
                  v-model="form.rememberMe"
                  label="Remember me"
                  color="primary"
                  dark
                  hide-details
                  class="remember-checkbox"
                />
                <v-btn
                  text
                  dark
                  class="forgot-link login-text-btn"
                  @click="showForgotPassword = true"
                >
                  Forgot password?
                </v-btn>
              </div>

              <!-- Submit Button -->
              <v-btn
                type="submit"
                color="primary"
                dark
                block
                :loading="loading"
                :disabled="!valid || loading"
                class="mb-4 submit-btn login-submit-btn"
                elevation="2"
              >
                <span v-if="!loading">Sign in</span>
                <span v-else>Signing in...</span>
              </v-btn>


            </v-form>

            <!-- Registration Link -->
            <div class="registration-link">
              <div class="registration-content">
                <p class="registration-text">
                  Don't have an account?
                  <button
                    type="button"
                    class="signup-text-link"
                    @click="$router.push('/register')"
                  >
                    Create Account
                  </button>
                </p>
                <p class="version-text">v1.8.05.26</p>
              </div>
            </div>
          </div>
        </div>

        <!-- PWA Install Prompt -->
        <PWAInstallPrompt />
      </div>

      <!-- Install App Button (Mobile) — outside login card -->
      <div v-if="showInstallButton" class="install-button-outside">
        <v-btn
          block
          color="primary"
          dark
          elevation="2"
          class="install-app-btn"
          @click="showInstallDialog = true"
        >
          <v-icon left>mdi-download</v-icon>
          Install App
        </v-btn>
      </div>
    </div>

    <footer class="login-footer">
      <div class="login-footer__inner">
        <router-link to="/privacy" class="login-footer__link">Privacy Policy</router-link>
        <span class="login-footer__sep" aria-hidden="true">·</span>
        <router-link to="/terms" class="login-footer__link">Terms of Service</router-link>
      </div>
    </footer>

    <!-- Install App Dialog -->
    <v-dialog v-model="showInstallDialog" max-width="500" persistent>
      <v-card dark>
        <v-card-title class="headline">
          <v-icon class="mr-2" color="primary">mdi-cellphone-link</v-icon>
          Install Cuetrics
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="mb-4">
            Install the app on your device for quick access and push notifications.
          </p>
          
          <!-- iOS Instructions -->
          <div v-if="isIOS" class="install-instructions">
            <h3 class="mb-3">iPhone/iPad:</h3>
            <div class="step-list">
              <div class="step-item">
                <v-icon size="20" class="step-icon">mdi-share-variant</v-icon>
                <span>Tap the <strong>Share</strong> button at the bottom</span>
              </div>
              <div class="step-item">
                <v-icon size="20" class="step-icon">mdi-plus-circle</v-icon>
                <span>Scroll and tap <strong>"Add to Home Screen"</strong></span>
              </div>
              <div class="step-item">
                <v-icon size="20" class="step-icon">mdi-check</v-icon>
                <span>Tap <strong>"Add"</strong> to confirm</span>
              </div>
            </div>
          </div>

          <!-- Android/Chrome Instructions -->
          <div v-else class="install-instructions">
            <h3 class="mb-3">Android/Chrome:</h3>
            <div class="step-list">
              <div class="step-item">
                <v-icon size="20" class="step-icon">mdi-menu</v-icon>
                <span>Tap the <strong>menu</strong> icon (3 dots) in the top right</span>
              </div>
              <div class="step-item">
                <v-icon size="20" class="step-icon">mdi-download</v-icon>
                <span>Look for <strong>"Install"</strong> or <strong>"Add to Home Screen"</strong></span>
              </div>
              <div class="step-item">
                <v-icon size="20" class="step-icon">mdi-check</v-icon>
                <span>Tap <strong>"Install"</strong> to add the app</span>
              </div>
            </div>
            <v-alert type="info" outlined dense class="mt-4">
              <small>If you don't see the install option, the browser may show it automatically, or you may need to use HTTPS.</small>
            </v-alert>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showInstallDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Forgot Password Dialog -->
    <v-dialog v-model="showForgotPassword" max-width="400" persistent>
      <v-card class="forgot-dialog" dark>
        <v-card-title class="dialog-title">
          Reset your password
        </v-card-title>
        <v-card-text class="dialog-content">
          <p class="dialog-description">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          <v-text-field
            v-model="forgotPasswordEmail"
            label="Email address"
            type="email"
            outlined
            dense
            dark
            class="login-field"
            :rules="[rules.required, rules.email]"
            placeholder="Enter your email"
          />
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn text color="primary" @click="showForgotPassword = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="reset-link-btn login-submit-btn"
            @click="handleForgotPassword"
            :loading="forgotPasswordLoading"
            :disabled="forgotPasswordLoading"
          >
            Send reset link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import { url } from '../../client_config/config';
import cuetricsLogo from '@/assets/cuetrics-light.png';
import loginHeroImage from '@/assets/login-hero-stage.jpg';
import PWAInstallPrompt from '../PWAInstallPrompt.vue';

export default {
  name: 'UserLogin',
  components: {
    PWAInstallPrompt
  },
  data() {
    return {
      cuetricsLogo,
      loginHeroImage,
      valid: false,
      loading: false,
      showPassword: false,
      showForgotPassword: false,
      showInstallDialog: false,
      forgotPasswordLoading: false,
      forgotPasswordEmail: '',
      isIOS: false,
      isStandalone: false,
      form: {
        email: '',
        password: '',
        rememberMe: false
      },
      rules: {
        required: v => !!v || 'This field is required',
        email: v => /.+@.+\..+/.test(v) || 'Please enter a valid email address'
      }
    };
  },
  computed: {
    ...mapGetters('auth', ['hasError']),
    
    error() {
      return this.$store.state.auth.error;
    },
    
    showInstallButton() {
      // Show install button on mobile devices if not already installed
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      return isMobile && !this.isStandalone;
    }
  },
  mounted() {
    this.detectPlatform();
  },
  methods: {
    ...mapActions('auth', ['login']),
    
    detectPlatform() {
      // Detect iOS
      this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      
      // Detect if already installed (standalone mode)
      this.isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone ||
                         document.referrer.includes('android-app://');
    },

    async handleSubmit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      
      try {
        // Clear any stale tokens before attempting login
        this.$store.dispatch('auth/logout');
        
        const result = await this.$store.dispatch('auth/login', {
          email: this.form.email,
          password: this.form.password
        });
        
        if (result && result.success) {
          this.$store.dispatch('showSuccess', 'Login successful!');
          
          // Redirect to role-specific dashboard
          const userType = result.user.userType;
          const routeMap = {
            'super_admin': '/super-admin/dashboard',
            'admin': '/admin/dashboard',
            'judge': '/judge/dashboard',
            'performer': '/performer/dashboard',
            'production_manager': '/production-manager/dashboard',
            'fundraising_leader': '/fundraising-leader/fundraising'
          };
          const dashboardRoute = routeMap[userType] || '/dashboard';
          this.$router.push(dashboardRoute);
        } else {
          // Login didn't return success
          this.$store.dispatch('showError', 'Login failed. Please check your credentials.');
        }
      } catch (error) {
        // Ensure we're logged out on error
        this.$store.dispatch('auth/logout');
        const errorMessage = error.response?.data?.message || error.message || 'Login failed. Please check your credentials.';
        this.$store.dispatch('showError', errorMessage);
      } finally {
        this.loading = false;
      }
    },

    async handleForgotPassword() {
      if (!this.forgotPasswordEmail || !/.+@.+\..+/.test(this.forgotPasswordEmail)) {
        this.$store.dispatch('showError', 'Please enter a valid email address');
        return;
      }

      this.forgotPasswordLoading = true;
      
      try {
        const response = await axios.post(`${url}/auth/forgot-password`, {
          email: this.forgotPasswordEmail
        });
        
        if (response.data.success) {
          this.$store.dispatch('showSuccess', 'Password reset link sent to your email');
          this.showForgotPassword = false;
          this.forgotPasswordEmail = '';
        } else {
          this.$store.dispatch('showError', response.data.message || 'Failed to send reset link. Please try again.');
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to send reset link. Please try again.';
        this.$store.dispatch('showError', errorMessage);
      } finally {
        this.forgotPasswordLoading = false;
      }
    },


  }
};
</script>

<style>
input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear {
  display: none !important;
}
</style>

<style scoped>
.login-page {
  --brand-navy: #12121a;
  --brand-navy-mid: #151028;
  --login-hero-image: none;

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--brand-stage);
  color: #fff;
  position: relative;
  overflow: hidden;
}

/*
 * Base = solid stage color. Photo sits in ::before and fades out via mask
 * (not an opacity gradient), so there is no vertical band at 50%.
 */
.login-page__backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-color: var(--brand-stage);
}

.login-page__backdrop::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: var(--login-hero-image);
  background-size: cover;
  background-position: 18% center;
  background-repeat: no-repeat;
  -webkit-mask-image: linear-gradient(
    90deg,
    #000 0%,
    #000 36%,
    rgba(0, 0, 0, 0.92) 46%,
    rgba(0, 0, 0, 0.65) 56%,
    rgba(0, 0, 0, 0.28) 68%,
    transparent 82%
  );
  mask-image: linear-gradient(
    90deg,
    #000 0%,
    #000 36%,
    rgba(0, 0, 0, 0.92) 46%,
    rgba(0, 0, 0, 0.65) 56%,
    rgba(0, 0, 0, 0.28) 68%,
    transparent 82%
  );
}

.login-page__backdrop::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(21, 16, 40, 0.42) 0%,
    rgba(21, 16, 40, 0.22) 32%,
    rgba(21, 16, 40, 0.08) 55%,
    transparent 75%
  );
  pointer-events: none;
}

.background-gradient {
  display: none;
}

.login-main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
  z-index: 2;
  width: 100%;
}

.login-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  z-index: 2;
}

/* Hero Section */
.hero-section {
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 3rem;
  position: relative;
  min-width: 0;
}

.hero-content {
  max-width: 500px;
  color: white;
  position: relative;
  z-index: 3;
}

.brand-section {
  margin-bottom: 3rem;
}

.logo-container {
  margin-bottom: 2rem;
}

.logo-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: white;
}

.accent-text {
  background: var(--brand-gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.125rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
}

.features-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: rgba(10, 10, 15, 0.42);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.feature-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  margin-top: 1px;
  border-radius: 10px;
  background: rgba(96, 88, 200, 0.45);
  border: 1px solid rgba(159, 152, 232, 0.35);
}

.feature-icon-wrap ::v-deep .v-icon,
.feature-icon {
  color: #fff !important;
}

.feature-content {
  flex: 1;
}

.feature-title {
  font-size: 1rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.25rem;
}

.feature-description {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
}

/* Form Section — desktop card aligns with hero column (.hero-content max-width: 500px) */
.form-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 2.5rem 3rem;
  position: relative;
  background: transparent;
}

.form-container {
  width: 100%;
  max-width: 500px;
  padding: 3rem 2.75rem 3.25rem;
  min-height: min(580px, 72vh);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  background: var(--app-elevated-surface);
  border: 1px solid var(--table-surface-border);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}

.form-header {
  text-align: center;
  margin-bottom: 2.25rem;
}

.form-brand-logo {
  display: block;
  margin-left: auto;
  margin-right: auto;
  max-width: 100%;
  width: 280px;
  height: auto;
  margin-bottom: 1.5rem;
}

.form-title {
  font-size: 2.125rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
}

.form-subtitle {
  font-size: 1.0625rem;
  color: rgba(255, 255, 255, 0.55);
}

.login-form {
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}




.error-alert {
  margin-bottom: 1rem;
  border-radius: 8px;
  background-color: rgba(220, 38, 38, 0.12) !important;
  border: 1px solid rgba(248, 113, 113, 0.35) !important;
  color: #fca5a5 !important;
  padding: 12px 16px !important;
  font-weight: 500 !important;
  line-height: 1.4 !important;
}

.remember-checkbox ::v-deep .v-label {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.875rem;
}

/* Gecko (Firefox/Waterfox) renders its own native password-reveal control,
   so hide our custom eye toggle there to avoid a duplicate icon. Other
   engines (WebKit/Blink) have no native reveal, so they keep our toggle. */
@supports (-moz-appearance: none) {
  .login-field ::v-deep .v-input__icon--append {
    display: none;
  }
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: nowrap;
  gap: 0.5rem;
}

.remember-checkbox {
  margin: 0;
  flex-shrink: 0;
}

.remember-checkbox ::v-deep .v-input__control {
  min-width: auto;
}

.remember-checkbox ::v-deep .v-input__slot {
  margin-bottom: 0;
}

.forgot-link {
  font-size: 0.875rem;
  text-transform: none;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
  padding: 0.25rem 0.5rem !important;
  min-width: auto !important;
}

.login-text-btn ::v-deep .v-btn__content {
  color: var(--brand-lavender) !important;
}

.login-text-btn:hover ::v-deep .v-btn__content {
  color: #fff !important;
}

.submit-btn,
.login-submit-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  font-size: 0.9375rem;
  height: 44px;
  margin-bottom: 1rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.login-submit-btn ::v-deep .v-btn__content {
  color: #fff !important;
}

.login-submit-btn:not(.v-btn--disabled) {
  background: var(--brand-gradient-primary) !important;
  border-color: transparent !important;
  color: #fff !important;
  box-shadow: 0 4px 16px rgba(96, 88, 200, 0.45) !important;
}

.login-submit-btn:not(.v-btn--disabled) ::v-deep .v-btn__content,
.login-submit-btn:not(.v-btn--disabled) ::v-deep .v-icon {
  color: #fff !important;
}

.login-submit-btn:not(.v-btn--disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(96, 88, 200, 0.55) !important;
}

.login-submit-btn.v-btn--disabled {
  background: rgba(96, 88, 200, 0.25) !important;
  border: 1px solid rgba(159, 152, 232, 0.35) !important;
  opacity: 1 !important;
  box-shadow: none !important;
}

.login-submit-btn.v-btn--disabled ::v-deep .v-btn__content {
  color: rgba(255, 255, 255, 0.65) !important;
}

.divider {
  position: relative;
  text-align: center;
  margin: 1.5rem 0;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--table-surface-border);
}

.divider-text {
  background: var(--app-elevated-surface);
  padding: 0 1rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.875rem;
}

.social-login {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.social-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  height: 44px;
  border: 1px solid var(--table-surface-border);
}

.google-btn:hover {
  border-color: #db4437;
  color: #db4437;
}

.facebook-btn:hover {
  border-color: #4267B2;
  color: #4267B2;
}

.registration-link {
  text-align: center;
  padding-top: 1.25rem;
  border-top: 1px solid var(--table-surface-border);
  display: flex;
  justify-content: center;
  align-items: center;
}

.registration-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.registration-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
}

.signup-text-link {
  display: inline;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  color: var(--brand-lavender);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}

.signup-text-link:hover {
  color: #fff;
  text-decoration: underline;
}

.version-text {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
  padding-top: 0.25rem;
}

.install-button-outside {
  display: none;
}

.install-app-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
  height: 44px;
  background: var(--brand-purple) !important;
  box-shadow: 0 4px 14px rgba(96, 88, 200, 0.45);
}

.install-app-btn ::v-deep .v-btn__content {
  color: #fff !important;
}

.install-app-btn ::v-deep .v-icon {
  color: #fff !important;
}

.install-app-btn:hover {
  background: var(--brand-lavender) !important;
}

.install-instructions h3 {
  font-size: 1rem;
  font-weight: 600;
  color: white;
}

.step-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-size: 0.875rem;
  line-height: 1.5;
}

.step-icon {
  color: rgba(255, 255, 255, 0.9);
  flex-shrink: 0;
}

/* Dialog Styles */
.forgot-dialog {
  border-radius: 12px;
  background: var(--app-elevated-surface) !important;
  border: 1px solid var(--table-surface-border);
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  padding-bottom: 0.5rem;
}

.dialog-content {
  padding-top: 0;
}

.dialog-description {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.dialog-actions {
  padding: 1rem 1.5rem 1.5rem;
}

.reset-link-btn {
  text-transform: none;
  font-weight: 600;
}

@media (min-width: 769px) {
  .form-section {
    padding-left: 1.5rem;
  }
}

/* Tablet: stack columns but keep unified backdrop */
@media (max-width: 1024px) and (min-width: 769px) {
  .login-container {
    grid-template-columns: 1fr;
  }

  .hero-section {
    min-height: 42vh;
    padding: 3rem 2rem 2rem;
  }

  .login-page__backdrop::before {
    -webkit-mask-image: linear-gradient(
      180deg,
      #000 0%,
      #000 30%,
      rgba(0, 0, 0, 0.55) 50%,
      transparent 72%
    );
    mask-image: linear-gradient(
      180deg,
      #000 0%,
      #000 30%,
      rgba(0, 0, 0, 0.55) 50%,
      transparent 72%
    );
  }
}

/* Mobile Responsive */
@media (max-width: 1024px) {
  .hero-section {
    padding: 3rem 2rem;
    min-height: 50vh;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .form-section {
    padding: 2rem 1rem;
  }
}

@media (max-width: 768px) {
  .login-container {
    display: flex;
    flex-direction: column;
  }

  .login-page__backdrop::before {
    background-position: center 20%;
    -webkit-mask-image: linear-gradient(
      180deg,
      #000 0%,
      #000 28%,
      rgba(0, 0, 0, 0.5) 48%,
      transparent 68%
    );
    mask-image: linear-gradient(
      180deg,
      #000 0%,
      #000 28%,
      rgba(0, 0, 0, 0.5) 48%,
      transparent 68%
    );
  }

  .login-page__backdrop::after {
    background: linear-gradient(
      180deg,
      rgba(21, 16, 40, 0.35) 0%,
      rgba(21, 16, 40, 0.12) 40%,
      transparent 65%
    );
  }

  .login-page {
    background: var(--brand-stage);
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem;
    padding-bottom: max(1.25rem, env(safe-area-inset-bottom));
    position: relative;
    overflow: hidden;
    z-index: 0;
  }

  .login-page::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        ellipse 100% 70% at 50% 30%,
        rgba(159, 152, 232, 0.18),
        transparent 60%
      );
    z-index: 0;
    pointer-events: none;
  }

  .login-page::after {
    display: none;
  }

  .background-gradient {
    display: none;
  }

  .login-main {
    flex: 1;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .login-container {
    flex: none;
    flex-direction: column;
    width: 100%;
    max-width: 420px;
    background: rgba(22, 20, 31, 0.92);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    box-shadow:
      0 0 0 1px rgba(96, 88, 200, 0.08),
      0 20px 50px rgba(0, 0, 0, 0.45);
    overflow: hidden;
    min-height: auto;
    position: relative;
    z-index: 1;
  }

  .install-button-outside {
    display: block;
    width: 100%;
    max-width: 420px;
    margin-top: 0.875rem;
    position: relative;
    z-index: 2;
    align-self: center;
  }

  .hero-section {
    display: none;
  }
  
  .form-section {
    padding: 2rem 1.75rem 1.75rem;
    background: transparent;
    width: 100%;
    border-radius: 0;
    position: relative;
    z-index: 1;
  }

  .form-container {
    max-width: 100%;
    min-height: 0;
    padding: 0;
    margin-bottom: 0;
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
    display: block;
  }

  .form-header {
    text-align: center;
    margin-bottom: 1.75rem;
  }

  .form-brand-logo {
    width: 220px;
    margin-bottom: 1rem;
  }

  .form-title {
    font-size: 1.75rem;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.55);
  }

  .login-form {
    margin-bottom: 0.5rem;
  }

  .login-form ::v-deep .login-field.mb-4 {
    margin-bottom: 1.125rem !important;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-options {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.25rem;
    margin-bottom: 1.75rem;
    flex-wrap: nowrap;
  }

  .submit-btn.login-submit-btn {
    margin-bottom: 1.25rem !important;
  }

  .remember-checkbox {
    margin: 0;
  }

  .forgot-link {
    font-size: 0.875rem;
    text-transform: none;
    font-weight: 500;
  }



  .divider {
    position: relative;
    text-align: center;
    margin: 1.5rem 0;
  }

  .divider::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--table-surface-border);
  }

  .divider-text {
    background: var(--app-elevated-surface);
    padding: 0 1rem;
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.875rem;
  }

  .social-login {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .social-btn {
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    height: 44px;
    border: 1px solid var(--table-surface-border);
  }

  .action-buttons {
    display: none;
  }

  .registration-link {
    text-align: center;
    padding-top: 1.25rem;
    margin-top: 0.25rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .registration-content {
    gap: 0.75rem;
  }

  .version-text {
    padding-top: 0;
  }

  .registration-text {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.55);
    margin: 0;
  }

  .version-text {
    color: rgba(255, 255, 255, 0.4);
  }
}

.login-footer {
  position: relative;
  z-index: 3;
  flex-shrink: 0;
  width: 100%;
  padding: 14px 24px max(14px, env(safe-area-inset-bottom));
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(10, 10, 15, 0.85);
  backdrop-filter: blur(8px);
}

.login-footer__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 960px;
  margin: 0 auto;
}

.login-footer__link {
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
}

.login-footer__link:hover {
  color: var(--brand-lavender);
  text-decoration: underline;
}

.login-footer__sep {
  color: rgba(255, 255, 255, 0.35);
}

@media (max-width: 480px) {
  .login-page {
    padding: 0.75rem;
    padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
  }

  .login-container {
    border-radius: 16px;
    max-width: 100%;
  }

  .install-button-outside {
    max-width: 100%;
  }
  
  .form-section {
    padding: 1.5rem 1.25rem 1.5rem;
  }
  
  .form-title {
    font-size: 1.5rem;
  }

  .form-subtitle {
    font-size: 0.8rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }
  
  .form-options {
    gap: 0.25rem;
  }
  
  .remember-checkbox ::v-deep .v-input__slot {
    padding: 0 !important;
  }
  
  .forgot-link {
    font-size: 0.75rem;
    padding: 0.25rem !important;
  }

  .submit-btn {
    height: 44px;
    margin-bottom: 2rem;
    font-size: 0.9rem;
  }

  .social-btn {
    height: 40px;
    font-size: 0.9rem;
  }

  .login-footer {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style> 